import { useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useDispatch } from 'react-redux';

import { IdentityProvider } from 'types';
import { addMinutes } from 'utils';
import { loaderActions, sessionActions } from 'store/actions';
import { useRouter } from 'hooks';
import { Navbar } from 'containers';
import { Divider } from 'components';
import { FacebookCircleLogoIcon, GoogleLogoIcon } from 'components/Icon';

import Confirm from './Authentication.Confirm';
import Login from './Authentication.Login';
import SignUp from './Authentication.SignUp';

import styles from './Authentication.module.scss';

export type AuthenticationProps = {};

export enum AuthenticationStep {
  LOGIN = 'LOGIN',
  SIGN_UP = 'SIGN_UP',
  CONFIRM = 'CONFIRM'
}

const Authentication = (props: AuthenticationProps): JSX.Element => {
  const router = useRouter();
  const dispatch = useDispatch();

  const [step, setStep] = useState<AuthenticationStep>(AuthenticationStep.LOGIN);
  const [redirecting, setRedirecting] = useState<'GOOGLE' | 'FACEBOOK'>();
  const usernameRef = useRef('');

  const handleOnSignUp = (_username: string) => {
    const timerExpirationTime = addMinutes(1);

    dispatch(sessionActions.setOTPCountdownTime(timerExpirationTime.toString()));

    usernameRef.current = _username;
    setStep(AuthenticationStep.CONFIRM);
  };

  const handleUserNotConfirmed = (_username: string) => {
    const timerExpirationTime = addMinutes(1);

    dispatch(sessionActions.setOTPCountdownTime(timerExpirationTime.toString()));

    usernameRef.current = _username;
    setStep(AuthenticationStep.CONFIRM);
  };

  const handleOnConfirm = () => {
    dispatch(loaderActions.showLoader());
    router.replace('/');
  };

  const handleOnResendCode = () => {
    const timerExpirationTime = addMinutes(1);

    dispatch(sessionActions.setOTPCountdownTime(timerExpirationTime.toString()));
  };

  const handleOnLogin = () => router.replace('/');

  const showSignUp = () => setStep(AuthenticationStep.SIGN_UP);

  const showLogin = () => setStep(AuthenticationStep.LOGIN);

  const handleOnSignInWithFacebook = async () => {
    try {
      setRedirecting('FACEBOOK');
      // TODO: investigate amplify types
      // @ts-ignore
      await Auth.federatedSignIn({ provider: IdentityProvider.FACEBOOK });
    } catch (e) {
      setRedirecting(undefined);
    }
  };

  const handleOnSignInWithGoogle = async () => {
    try {
      setRedirecting('GOOGLE');
      // TODO: investigate amplify types
      // @ts-ignore
      await Auth.federatedSignIn({ provider: IdentityProvider.GOOGLE });
    } catch (e) {
      setRedirecting(undefined);
    }
  };

  const renderTitle = () => {
    switch (step) {
      case AuthenticationStep.LOGIN:
        return 'Login';

      case AuthenticationStep.SIGN_UP:
        return 'Sign up';

      case AuthenticationStep.CONFIRM:
        return 'Confirm';

      default:
        return null;
    }
  };

  const renderContent = () => {
    switch (step) {
      case AuthenticationStep.SIGN_UP:
        return <SignUp onSubmitted={handleOnSignUp} onOpenLogin={showLogin} />;

      case AuthenticationStep.LOGIN:
        return (
          <Login onSubmitted={handleOnLogin} onOpenSignUp={showSignUp} onUserNotConfirmed={handleUserNotConfirmed} />
        );

      case AuthenticationStep.CONFIRM:
        return (
          <Confirm username={usernameRef.current} onSubmitted={handleOnConfirm} onCodeResent={handleOnResendCode} />
        );

      default:
        return null;
    }
  };

  console.log('DEBUG: ', { redirecting });

  return (
    <>
      <Navbar title={renderTitle()} />

      <div className={styles.container}>
        {renderContent()}
        <Divider text="Or" className={styles.separator} />
        <div className={styles.social}>
          <button
            type="button"
            disabled={redirecting === 'GOOGLE'}
            className={styles.google}
            onClick={handleOnSignInWithGoogle}
          >
            <GoogleLogoIcon />
          </button>
          <button
            type="button"
            disabled={redirecting === 'FACEBOOK'}
            className={styles.facebook}
            onClick={handleOnSignInWithFacebook}
          >
            <FacebookCircleLogoIcon />
          </button>
        </div>
      </div>
    </>
  );
};

export default Authentication;
